/**
 * The Repositories tab on the queue grammar: which group a repository's
 * environment sits in (by what the reader owes it), and how its row reads.
 * Pure, so the grouping is tested over fixtures without a network.
 */
import type { QaEnvironmentState, QaEnvironmentView } from "@yagni/shared";
import { environmentPill, formatEnvironmentDate } from "../settings/environmentPage.js";

export type RepoGroupKey = "needs" | "setting" | "working" | "notSetUp";

/** The part of a listed repository the row reads. */
export interface RepoRowSource {
  id: string;
  owner: string;
  name: string;
  environment: QaEnvironmentView;
  warning?: string;
}

export function repoGroupOf(environment: Pick<QaEnvironmentView, "state">): RepoGroupKey {
  switch (environment.state) {
    case "needs_you":
      return "needs";
    case "setting_up":
      return "setting";
    case "working":
      return "working";
    default:
      return "notSetUp";
  }
}

export function groupRepositories<T extends RepoRowSource>(repositories: readonly T[]): Record<RepoGroupKey, T[]> {
  const groups: Record<RepoGroupKey, T[]> = { needs: [], setting: [], working: [], notSetUp: [] };
  for (const repo of repositories) groups[repoGroupOf(repo.environment)].push(repo);
  for (const key of Object.keys(groups) as RepoGroupKey[])
    groups[key].sort((a, b) => `${a.owner}/${a.name}`.localeCompare(`${b.owner}/${b.name}`));
  return groups;
}

/** The status dot: blue and breathing while it waits on a person, green while it works. */
export function repoDot(state: QaEnvironmentState | undefined): { color: string; mode?: "wait" | "live" } {
  switch (state) {
    case "needs_you":
      return { color: "var(--accent)", mode: "wait" };
    case "setting_up":
      return { color: "var(--honesty)", mode: "live" };
    case "working":
      return { color: "var(--accent-active)" };
    default:
      return { color: "var(--text-quiet)" };
  }
}

/** The row's tag: the pill word the environment page uses. */
export function repoTag(environment: Pick<QaEnvironmentView, "state" | "buildOnly">): string {
  return environmentPill(environment.state, environment.buildOnly).label;
}

/** The owning Team, or "no Team": the row's key. */
export function repoTeamKey(environment: Pick<QaEnvironmentView, "uses">): string {
  const teams = environment.uses?.teams ?? [];
  if (teams.length === 0) return "no Team";
  return teams.length === 1 ? teams[0]!.name : `${teams[0]!.name} +${teams.length - 1}`;
}

/** The one-line reason under the row's title. */
export function repoReason(repo: RepoRowSource): string {
  if (repo.warning) return "Could not verify access just now";
  const environment = repo.environment;
  const validation = environment.latestRun?.validation;
  switch (environment.state) {
    case "needs_you":
      return validation?.cause ?? validation?.message ?? "The last run needs a person";
    case "setting_up":
      return validation?.message ?? "A run is setting the environment up";
    case "working": {
      if (environment.lastBuildFailure) return "A build could not install it; check again";
      const since = environment.working?.since;
      const lead = environment.buildOnly ? "Builds and tests run" : "Environment healthy";
      return since ? `${lead} · since ${formatEnvironmentDate(since)}` : lead;
    }
    default:
      return "Builds use the default image until this is set up";
  }
}

export function repoVerb(environment: Pick<QaEnvironmentView, "state">): string {
  switch (environment.state) {
    case "needs_you":
      return "Fix";
    case "not_set_up":
      return "Set up";
    default:
      return "Open";
  }
}

/** "index fresh · 2h", "indexing", or nothing when the server has no index. */
export function indexChip(
  index: { status: string; indexedAt: string | null } | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!index) return null;
  if (index.status !== "indexed") return index.status === "failed" ? "index failed" : "indexing";
  if (!index.indexedAt) return "indexed";
  const hours = Math.max(0, Math.floor((now - new Date(index.indexedAt).getTime()) / 3_600_000));
  if (!Number.isFinite(hours)) return "indexed";
  if (hours < 1) return "index fresh · just now";
  if (hours < 48) return `index fresh · ${hours}h`;
  return `indexed ${Math.floor(hours / 24)}d ago`;
}
